/**
 * Payment method types supported by QPilot
 * Based on old QMC qpilot-payment-method-type.ts and payment-integrations.helper.ts
 */
export enum QPilotPaymentMethodType {
  Stripe = 0,
  AuthorizeNet = 1,
  Test = 2,
  PayPal = 3,
  PaypalV2 = 4,
  Braintree = 5,
  Undefined = 6,
  Other = 7,
  Shopify = 8,
  Airwallex = 9,
  Checkout = 10,
  CyberSourceV2 = 11,
  Nmi = 12,
  PayaV1 = 13,
  TrustCommerce = 14,
  Square = 15,
  Sage = 16,
  PayPalV3 = 17,
}

/**
 * Helper to convert string payment method type to enum
 */
export function getPaymentMethodTypeEnum(
  type: string,
): QPilotPaymentMethodType {
  switch (type) {
    case "Stripe":
      return QPilotPaymentMethodType.Stripe;
    case "AuthorizeNet":
      return QPilotPaymentMethodType.AuthorizeNet;
    case "Test":
      return QPilotPaymentMethodType.Test;
    case "PayPal":
      return QPilotPaymentMethodType.PayPal;
    case "PaypalV2":
      return QPilotPaymentMethodType.PaypalV2;
    case "Braintree":
      return QPilotPaymentMethodType.Braintree;
    case "Shopify":
      return QPilotPaymentMethodType.Shopify;
    case "Airwallex":
      return QPilotPaymentMethodType.Airwallex;
    case "Checkout":
      return QPilotPaymentMethodType.Checkout;
    case "CyberSourceV2":
      return QPilotPaymentMethodType.CyberSourceV2;
    case "Nmi":
      return QPilotPaymentMethodType.Nmi;
    case "PayaV1":
      return QPilotPaymentMethodType.PayaV1;
    case "TrustCommerce":
      return QPilotPaymentMethodType.TrustCommerce;
    case "Square":
      return QPilotPaymentMethodType.Square;
    case "Sage":
      return QPilotPaymentMethodType.Sage;
    case "PayPalV3":
      return QPilotPaymentMethodType.PayPalV3;
    default:
      return QPilotPaymentMethodType.Other;
  }
}

/**
 * QPilot Payment Method interface
 * Represents a payment method (credit card, PayPal, etc.) for a customer
 * Based on old QMC qpilot-payment-method.ts
 */
export interface QPilotPaymentMethod {
  // Core properties
  Id: number;
  CustomerId?: number;
  Description?: string;
  Type?: QPilotPaymentMethodType;

  // Gateway integration
  GatewayCustomerId?: string;
  GatewayPaymentId?: string;
  GatewayPaymentType?: number | string; // Can be number or string (e.g., "card" for Stripe)

  // Billing address
  BillingFirstName?: string;
  BillingLastName?: string;
  BillingStreet1?: string;
  BillingStreet2?: string;
  BillingCity?: string;
  BillingState?: string;
  BillingPostcode?: string;
  BillingCountry?: string;

  // Payment details
  Expiration?: string;
  ExpirationMonth?: string;
  ExpirationYear?: string;
  LastFourDigits?: string;
  LastFour?: string; // Alias for LastFourDigits
  Last4?: string; // Another alias used in some API responses
  Brand?: string;

  // Audit fields
  CreatedUtc?: Date | string;
  UpdatedUtc?: Date | string;
}

/**
 * Payment integration configuration
 * Represents site-level payment gateway configuration
 */
export interface PaymentIntegration {
  Id: number;
  SiteId: number;
  PaymentMethodType: string;
  ApiAccount?: string;
  ApiKey1?: string;
  ApiKey2?: string;
  ApiKey3?: string;
  TestMode: boolean;
  Active: boolean;
  CreatedUtc: string;
  UpdatedUtc: string;
}

/**
 * Payment integration response from API
 */
export interface PaymentIntegrationsResponse {
  Items: PaymentIntegration[];
  TotalCount: number;
}

/**
 * Payment profile for loading from gateway
 * Used for manual profile loading workflow
 */
export interface PaymentProfile {
  Id: number;
  Type: QPilotPaymentMethodType;
  GatewayCustomerId: string;
  Description: string;
}
